// controllers
import AnimationController from '../../../assets/js/controller/animation';
import ResizeController from '../../../assets/js/controller/resize';
import ScrollController from '../../../assets/js/controller/scroll';

// helpers
import { bindEvents, getRootOffsetBounds } from '../../../assets/js/helpers';

// parallax images
import Image from '../../../bits/image/script';

const selector = 'hero-home';

export default class HeroHome {

	constructor(section) {
		this.el = {
			section,
			images: section.querySelectorAll(`.${selector}__image`),
			imageWrappers: section.querySelectorAll(`.${selector}__image-wrapper`),
			allImagesWrapper: section.querySelector(`.${selector}__images`)
		};

		// bind events to class scope
		this.events = bindEvents(this);

		// class variable groups
		this.config = {
			animationPauseGutter: 0, // % of bottom of screen where scrolling is paused
			maxSpeed: 0.06, // % vertical height per second
			acceleration: 0.06, // % vertical height per second
			deceleration: 0.06 // % vertical height per second
		};

		this.controllers = {
			animation: null,
			resize: null,
			scroll: null
		};

		this.bounds = {
			allImagesWrapper: null,
			imageWrappers: [],
			section: null
		};

		this.coords = {
			bottomMargin: 80,
			speed: {
				maxSpeed: 0,
				acceleration: 0,
				deceleration: 0
			}
		};

		this.flags = {
			isFirstImageMove: true,
			isMoving: false,
			isPendingMeasure: false
		};

		this.states = {
			shiftRatio: 0, // % of section height scrolled
			speed: 0, // pixels per second
			lastUpdateTime: 0
		};

		this.parallaxImages = [];

		// engage
		this.init();
	}

	static get selector() {
		return selector;
	}

	// initialize class

	init() {
		this.addEventListeners();
	}

	// event listeners

	addEventListeners() {
		this.controllers.animation = AnimationController.set({
			update: this.events._onUpdate,
			render: this.events._onRender
		});

		this.controllers.resize = ResizeController.set({
			update: () => { this.measureResize(); },
			resize: () => {}
		});

		this.controllers.scroll = ScrollController.set({
			update: this.events._onScrollUpdate,
			render: this.events._onScrollRender
		});

		// create parallax images

		for (let i = 0; i < this.el.images.length; i++) {
			const $parallaxImage = this.el.images[i];
			this.parallaxImages.push(new Image($parallaxImage));
		}

		this.measureResize();
	}

	removeEventListeners() {
		AnimationController.clear(this.controllers.animation);
		ResizeController.clear(this.controllers.resize);
		ScrollController.clear(this.controllers.scroll);

		for (let i = 0; i < this.parallaxImages.length; i++) {
			const parallaxImage = this.parallaxImages[i];
			parallaxImage.destroy();
		}

		this.parallaxImages = [];
	}

	// animation ticking

	_onUpdate() {
		if (!this.states.lastUpdateTime) {
			this.states.lastUpdateTime = Date.now();
		}

		const now = Date.now();
		const dt = (now - this.states.lastUpdateTime) / 1000;
		const lastShiftRatio = this.states.shiftRatio;

		if (this.flags.isMoving) {
			this.states.speed += this.coords.speed.acceleration * dt;
		} else {
			this.states.speed -= this.coords.speed.deceleration * dt;
		}

		this.states.speed = Math.max(0, Math.min(this.coords.speed.maxSpeed, this.states.speed));

		this.states.shiftRatio -= (this.states.speed * dt) / this.bounds.allImagesWrapper.height;

		if (this.flags.isPendingMeasure) {
			this.flags.isPendingMeasure = false;
			this.measureImages();
		}

		this.states.lastUpdateTime = now;

		return (this.states.shiftRatio !== lastShiftRatio);
	}

	_onRender() {
		this.moveImages();
	}

	// scrolling

	_onScrollUpdate() {
		this.measureScroll();
	}

	_onScrollRender() { }

	// measurement methods

	measureResize() {
		this.coords.section = getRootOffsetBounds(this.el.section);

		this.coords.speed.maxSpeed = this.config.maxSpeed * window.innerHeight;
		this.coords.speed.acceleration = this.config.acceleration * window.innerHeight;
		this.coords.speed.deceleration = this.config.deceleration * window.innerHeight;

		this.measureImages();
		this.measureScroll();
	}

	measureImages() {

		// individual image wrapper coordinates

		this.bounds.imageWrappers = [];
		for (let i = 0; i < this.el.imageWrappers.length; i++) {
			const $image = this.el.imageWrappers[i];
			this.bounds.imageWrappers.push(getRootOffsetBounds($image));
		}

		//

		for (let i = 0; i < this.parallaxImages.length; i++) {
			const parallaxImage = this.parallaxImages[i];
			parallaxImage.measureResize();
		}

		// total wrapper coordinates

		this.bounds.allImagesWrapper = getRootOffsetBounds(this.el.allImagesWrapper);

		// check images for loaded state

		for (let i = 0; i < this.el.images.length; i++) {
			const $image = this.el.images[i];
			if (!$image.classList.contains('is-hh-loading')) {
				$image.classList.add('is-hh-loading');
				if ($image.offsetHeight) {
					$image.classList.remove('is-hh-loading');
				} else {
					$image.querySelector('img').addEventListener('load', () => {
						$image.classList.remove('is-hh-loading');
						this.flags.isPendingMeasure = true;
					});
				}
			}
		}
	}

	measureScroll() {
		const scrollY = ScrollController.position;
		const scrollBottom = scrollY + window.innerHeight * this.config.animationPauseGutter;

		if (scrollBottom < this.coords.section.bottom) {
			this.flags.isMoving = true;
		} else {
			this.flags.isMoving = false;
		}
	}

	// move images

	moveImages() {
		const minBottomCoords = Math.min(this.coords.section.bottom, ScrollController.position + window.innerHeight) + this.coords.bottomMargin;

		if (ScrollController.position >= 0) {

			for (let i = 0; i < this.el.images.length; i++) {
				const $image = this.el.images[i];
				const $imageWrapper = this.el.imageWrappers[i];

				const imageWrapperCoords = this.bounds.imageWrappers[i];
				const parallaxImage = this.parallaxImages[i];
				const shiftY = this.states.shiftRatio * this.bounds.allImagesWrapper.height;
				const wrapperShiftY = Math.ceil((0 - shiftY - imageWrapperCoords.height) / this.bounds.allImagesWrapper.height) * this.bounds.allImagesWrapper.height;
				const scrollY = imageWrapperCoords.top + shiftY;

				// shift images vertically
				const shiftYRounded = Math.round(shiftY * 100) / 100;
				$image.style.transform = `translateY(${shiftYRounded}px)`;
				parallaxImage.renderScroll(scrollY + imageWrapperCoords.height / 2);

				// manually track scroll position
				$image.setAttribute('data-scroll-y', scrollY);

				if (imageWrapperCoords.bottom + shiftY < 0) {
					// if wrapper needs to move (due to being offscreen)
					const wrapperShiftYRounded = Math.round(wrapperShiftY * 100) / 100;
					$imageWrapper.style.top = `${wrapperShiftYRounded}px`;
					$imageWrapper.classList.remove('is-active');

					// update measurements
					this.measureImages();

				} else if (imageWrapperCoords.top + shiftY < minBottomCoords) {
					if (!$imageWrapper.classList.contains('is-active')) {
						$imageWrapper.classList.add('is-active');
					}
				} else {
					$imageWrapper.classList.remove('is-active');
				}
			}

			this.flags.isFirstImageMove = false;
		}
	}

	//

	destroy() {

	}

}
